import { lintFreeDsl } from './freeDslLinter';
import type { MetamodelContext } from './metamodelContext';
import { lintPlantUml, Severity } from './plantumlLinter';
import { lintStructurizrDsl } from './structurizrLinter';

export type PublishLintSeverity = 'error' | 'warning' | 'info';

export interface PublishLintDiagnostic {
  severity: PublishLintSeverity;
  message: string;
  line?: number;
  ruleId?: string;
}

export interface CollectPublishLintDiagnosticsArgs {
  mode?: string | null;
  code?: string | null;
  metamodelContext?: MetamodelContext | null;
  allowedParticipants?: string[];
  allowedNames?: string[];
}

type RawDiagnostic = {
  severity?: unknown;
  message?: unknown;
  line?: unknown;
  startLineNumber?: unknown;
  ruleId?: unknown;
  code?: unknown;
};

const normalizeSeverity = (severity: unknown): PublishLintSeverity => {
  if (severity === Severity.ERROR || severity === 'error') return 'error';
  if (severity === Severity.WARNING || severity === 'warning') return 'warning';
  return 'info';
};

export const normalizePublishLintDiagnostics = (diagnostics: RawDiagnostic[] = []): PublishLintDiagnostic[] =>
  diagnostics
    .map((item) => {
      const line = typeof item.line === 'number'
        ? item.line
        : typeof item.startLineNumber === 'number'
          ? item.startLineNumber
          : undefined;

      return {
        severity: normalizeSeverity(item.severity),
        message: String(item.message ?? '').trim(),
        line,
        ruleId: item.ruleId ? String(item.ruleId) : item.code ? String(item.code) : undefined,
      };
    })
    .filter((item) => item.message.length > 0);

export const collectBpmnPublishLintDiagnostics = (code?: string | null): PublishLintDiagnostic[] => {
  if (!code?.trim()) return [];

  const lines = code.split('\n');
  const diagnostics: PublishLintDiagnostic[] = [];
  const declaredVarLines = new Map<string, number>();
  const connectionVarLines = new Map<string, number[]>();
  const assignRe = /^\s*([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)\s*=/;
  const connRe = /^\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*--?>\s*([a-zA-Z_][a-zA-Z0-9_.]*)/;
  const keywords = new Set(['diagram', 'lane', 'labels']);
  let hasDiagramHeader = false;

  lines.forEach((rawLine, index) => {
    const line = rawLine.replace(/\/\/.*$/, '').trim();
    const lineNumber = index + 1;

    if (/^diagram\s+"/.test(line)) {
      hasDiagramHeader = true;
    }

    if (/^lane\s+"/.test(line) && !/<<[^>]+>>/.test(line)) {
      const laneName = line.match(/^lane\s+"([^"]+)"/)?.[1] ?? 'Unknown';
      diagnostics.push({
        severity: 'warning',
        message: `Lane "${laneName}" не синхронизирован ни с одной системой. Добавьте <<КОД_СИСТЕМЫ>> после названия lane.`,
        line: lineNumber,
        ruleId: 'UNLINKED_LANE',
      });
    }

    const assignMatch = line.match(assignRe);
    if (assignMatch && !keywords.has(assignMatch[1]) && !declaredVarLines.has(assignMatch[1])) {
      declaredVarLines.set(assignMatch[1], lineNumber);
    }

    const connMatch = line.match(connRe);
    if (connMatch) {
      [connMatch[1], connMatch[2]].forEach((varName) => {
        if (!connectionVarLines.has(varName)) {
          connectionVarLines.set(varName, []);
        }
        connectionVarLines.get(varName)?.push(lineNumber);
      });
    }
  });

  if (!hasDiagramHeader) return diagnostics;

  connectionVarLines.forEach((lineNumbers, varName) => {
    if (declaredVarLines.has(varName)) return;

    lineNumbers.forEach((lineNumber) => {
      diagnostics.push({
        severity: 'warning',
        message: `"${varName}" используется в связи, но не объявлен как элемент.`,
        line: lineNumber,
        ruleId: 'UNDECLARED_VAR',
      });
    });
  });

  if (connectionVarLines.size > 0) {
    declaredVarLines.forEach((lineNumber, varName) => {
      if (connectionVarLines.has(varName)) return;

      diagnostics.push({
        severity: 'info',
        message: `"${varName}" объявлен, но не используется ни в одной связи (->).`,
        line: lineNumber,
        ruleId: 'UNUSED_VAR',
      });
    });
  }

  return diagnostics;
};

export const collectPublishLintDiagnostics = ({
  mode,
  code,
  metamodelContext,
  allowedParticipants = [],
  allowedNames = [],
}: CollectPublishLintDiagnosticsArgs): PublishLintDiagnostic[] => {
  if (!code?.trim()) return [];

  switch (mode) {
    case 'uml':
    case 'archimate':
      return normalizePublishLintDiagnostics(lintPlantUml(code, { allowedParticipants }));
    case 'c4':
      return normalizePublishLintDiagnostics(lintStructurizrDsl(code, { allowedCodes: allowedParticipants, allowedNames }));
    case 'free':
      return normalizePublishLintDiagnostics(lintFreeDsl(code, metamodelContext ?? undefined));
    case 'bpmn':
      return collectBpmnPublishLintDiagnostics(code);
    case 'erd':
    default:
      return [];
  }
};
